import { db } from '../db';
import { batchUploadSession } from './scoutingService';
import { syncPendingPhenologyRecords } from './phenologySyncService';
import { syncPendingSprayCompletions } from './spraySyncService';
import { syncPendingFieldScout, resetFieldScoutSyncState } from './fieldScoutSyncService';
import { refreshWeatherCache } from './weatherCacheService';
import { refreshMySessions } from './spraySessionCacheService';
import { recordStartupSyncMs } from './startupTiming';
import { recordDeathAtLaunch } from './fieldScoutBackoff';

/** Pause before the first field-scout upload after launch, so the app is up first. */
export const FIELD_SCOUT_LAUNCH_DELAY_MS = 3000;

let syncInterval: NodeJS.Timeout | null = null;
let isSyncInProgress = false;

const QUARANTINE_THRESHOLD = 5;

/**
 * Resets sessions stuck in 'syncing' or 'quarantined' state back to 'pending',
 * allowing them to be retried. Clears retryCount on quarantined sessions.
 * Returns the number of sessions reset.
 */
export const resetSyncState = async (): Promise<number> => {
  let count = 0;

  count += await db.scoutingSessions
    .where('syncStatus')
    .equals('syncing')
    .modify({ syncStatus: 'pending' });

  count += await db.scoutingSessions
    .where('syncStatus')
    .equals('quarantined')
    .modify({ syncStatus: 'pending', retryCount: 0 });

  return count;
};

export const syncPendingSessions = async (
  options: { drainFieldScout?: boolean } = {},
): Promise<void> => {
  const { drainFieldScout = true } = options;
  // 1. Immediate lock before any async calls to prevent race conditions in the same tab
  if (isSyncInProgress) {
    return;
  }

  if (!navigator.onLine) {
    return;
  }

  isSyncInProgress = true;

  try {
    // 2. Find sessions that need syncing (quarantined sessions are intentionally excluded)
    const pendingSessions = await db.scoutingSessions
      .where('syncStatus')
      .anyOf(['pending', 'failed'])
      .toArray();

    if (pendingSessions.length === 0) {
      return;
    }

    console.log(`[SyncEngine] Starting sync for ${pendingSessions.length} sessions...`);

    for (const session of pendingSessions) {
      try {
        // 3. Mark as 'syncing' in DB to prevent other tabs/triggers from picking it up
        await db.scoutingSessions.update(session.id!, { syncStatus: 'syncing' });

        console.log(`[SyncEngine] Uploading session ${session.id} (UUID: ${session.uuid})...`);
        const success = await batchUploadSession(session);

        if (success) {
          console.log(`[SyncEngine] Successfully synced session ${session.id}`);
          await db.scoutingSessions.update(session.id!, {
            syncStatus: 'synced'
          });
        } else {
          const newRetryCount = (session.retryCount || 0) + 1;
          const newStatus = newRetryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
          if (newStatus === 'quarantined') {
            console.warn(`[SyncEngine] Session ${session.id} quarantined after ${newRetryCount} failed attempts.`);
          } else {
            console.warn(`[SyncEngine] Server rejected session ${session.id}. Attempt: ${newRetryCount}`);
          }
          await db.scoutingSessions.update(session.id!, {
            syncStatus: newStatus,
            retryCount: newRetryCount
          });
        }
      } catch (error) {
        const newRetryCount = (session.retryCount || 0) + 1;
        const newStatus = newRetryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
        console.error(`[SyncEngine] Network error for session ${session.id}:`, error);
        await db.scoutingSessions.update(session.id!, {
          syncStatus: newStatus,
          retryCount: newRetryCount
        });
      }
    }
    // Sync pending phenology records alongside scouting sessions
    await syncPendingPhenologyRecords();
    // Drain field-scout batch queue alongside scouting sessions. The launch
    // call opts out: its field-scout drain is the delayed one in initSyncEngine.
    if (drainFieldScout) {
      await syncPendingFieldScout();
    }
  } finally {
    isSyncInProgress = false;
  }
};

/**
 * Initializes the background sync engine.
 */
export const initSyncEngine = async () => {
  if (typeof window === 'undefined') return;

  console.log('[SyncEngine] Initializing background synchronization...');

  // Timed for the Phone check page: everything up to the end of orphan
  // recovery, recorded even when a step throws.
  const startupT0 = performance.now();
  try {
    // A leftover upload marker means the last launch died mid-upload: count it
    // before the state reset below.
    recordDeathAtLaunch();

    // Reset any sessions stuck in 'syncing' (e.g. from a previous crash/refresh)
    await db.scoutingSessions
      .where('syncStatus')
      .equals('syncing')
      .modify({ syncStatus: 'pending' });

    // Reset any field-scout queue items stuck in 'syncing' from a previous crash.
    // (resetFieldScoutSyncState also handles quarantined→pending, mirroring the
    // scoutingSessions reset above.)
    await resetFieldScoutSyncState();
  } finally {
    recordStartupSyncMs(performance.now() - startupT0);
  }

  // Background refresh trigger that doesn't crash the engine on failure
  // (offline is the common cause; a failed refresh just leaves the cache
  // stale, which is exactly what the cache is for).
  const triggerSpraySessionRefresh = () => {
    refreshMySessions().catch(err => {
      console.warn('[SyncEngine] Spray session cache refresh failed:', err?.message);
    });
  };

  // 1. Listen for the 'online' event
  window.addEventListener('online', () => {
    console.log('[SyncEngine] Network status: ONLINE');
    syncPendingSessions();
    syncPendingSprayCompletions();
    syncPendingFieldScout();
    refreshWeatherCache();
    triggerSpraySessionRefresh();
  });

  // 2. Setup heartbeat interval (every 30 seconds)
  if (!syncInterval) {
    let cacheRefreshTick = 0;
    syncInterval = setInterval(() => {
      syncPendingSessions();
      syncPendingSprayCompletions();
      syncPendingFieldScout();
      refreshWeatherCache();
      // Spray session cache refreshes on a 60s cadence (every 2nd
      // heartbeat) — it costs a network round trip and only the My
      // Sessions screen consumes its output, so a slower beat keeps
      // mobile data usage low while still feeling fresh.
      cacheRefreshTick += 1;
      if (cacheRefreshTick % 2 === 0) {
        triggerSpraySessionRefresh();
      }
    }, 30000);
  }

  // 3. Initial check
  syncPendingSessions({ drainFieldScout: false });
  syncPendingSprayCompletions();
  setTimeout(() => {
    syncPendingFieldScout();
  }, FIELD_SCOUT_LAUNCH_DELAY_MS);
  refreshWeatherCache();
  triggerSpraySessionRefresh();
};
